import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PatientService } from '../patient.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { environment } from '../../../environments/environment';
import {
  BreedItem,
  ColorItem,
  SpeciesItem,
  VbExtendedPatient,
} from '@appyvet/vetbooker-definitions/dist/patient';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { AuthService } from 'src/app/auth/auth.service';
import { SessionService } from 'src/app/services/session-service.service';

export interface PetFormItem {
  patient: VbExtendedPatient;
  form: UntypedFormGroup;
  id: string;
  onlyUpdateForm?: boolean;
}

@Component({
  selector: 'app-add-patient-page',
  templateUrl: './add-patient-page.component.html',
  styleUrls: ['./add-patient-page.component.scss'],
})
export class AddPatientPageComponent implements OnInit, OnDestroy {
  editPatient: VbExtendedPatient;
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>(null);
  alphanumericRegexp: string;
  colors: ColorItem[];
  breeds: BreedItem[];
  species: SpeciesItem[];
  private speciesSubscription: Subscription;
  private colorsSubscription: Subscription;
  private breedsSubscription: Subscription;
  showLastVac: boolean;
  showInsured: boolean;
  showColors: boolean;
  showMicrochip: boolean;
  showIsPetImported: boolean;
  private clinicName: string;
  protected clinicCode: string;
  protected clinicPmsName: string;
  constructor(
    private patientService: PatientService,
    private route: ActivatedRoute,
    private router: Router,
    private gaService: GoogleAnalyticsService,
    private authService: AuthService,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: ParamMap) => {
      this.editPatient = this.patientService.getPatient(
        params.get('patientNumber'),
      );
    });
    this.clinicName = this.patientService.clinicName;
    this.clinicCode = this.authService.getClinicCode();
    this.clinicPmsName = this.authService.getPmsName();
    this.alphanumericRegexp = this.patientService.alphanumericRegexp;
    this.breedsSubscription = this.patientService.breeds$.subscribe(
      (breeds) => {
        this.breeds = breeds;
      },
    );
    this.colorsSubscription = this.patientService.colors$.subscribe(
      (colors) => {
        this.colors = colors;
      },
    );
    this.speciesSubscription = this.patientService.species$.subscribe(
      (species) => {
        this.species = species;
      },
    );

    this.showInsured =
      !this.patientService.isGroomRoom &&
      !this.editPatient &&
      this.patientService.showLastInsured;
    this.showLastVac =
      !this.patientService.isGroomRoom &&
      !this.editPatient &&
      this.patientService.showLastVac;
    if (!environment.VETS_4_PETS && !environment.GROOM) {
      this.showColors = this.patientService.showColors;
    }
    this.showMicrochip = this.patientService.showMicrochip;
    this.showIsPetImported = this.patientService.showIsPetImported;
  }

  async savePatient(petForm: PetFormItem) {
    this.loading$.next(true);
    this.error$.next(null);
    try {
      if (this.editPatient) {
        this.gaService.event('Edit pet', 'Pet details', this.clinicName);
        await this.patientService.editPatient({
          previousData: this.editPatient,
          updatedData: petForm.patient,
          patientNumber: this.editPatient.patientNumber,
        });
      } else {
        this.gaService.event('Add pet', 'Pet details', this.clinicName);
        await this.patientService.addPatient({ patientData: petForm.patient });
      }
      this.loading$.next(false);
      this.router.navigateByUrl('/pets');
    } catch (e) {
      this.error$.next(e);
      this.loading$.next(false);
    }
  }

  ngOnDestroy(): void {
    this.speciesSubscription?.unsubscribe();
    this.breedsSubscription?.unsubscribe();
    this.colorsSubscription?.unsubscribe();
  }
}
